/**
 * @fairway/content — every player-facing string and the locale layer (`25_copy_and_tone.md`
 * §3.18, TON-032 to TON-034).
 *
 * - Strings are ICU MessageFormat entries in `locales/en-GB/*.json`; code never builds a
 *   sentence from fragments (TON-032).
 * - The game is named by the `{gameName}` token, filled from `@fairway/shared`'s `GAME_NAME`
 *   (D-01). No string holds the name itself.
 * - Lengths, speeds and temperatures arrive in SI and are converted by
 *   `@fairway/shared/units`; a template takes the unit as a parameter (TON-033).
 * - Dates and times are shown in the player's own time zone from a stored UTC time, through
 *   this layer and never in a component (TON-034).
 */
import i18next, { type i18n } from 'i18next';
import ICU from 'i18next-icu';
import {
  GAME_NAME,
  displayLength,
  displaySpeed,
  displayTemperature,
  type LengthPurpose,
  type LengthSystem,
  type SpeedUnit,
  type TemperatureUnit,
} from '@fairway/shared';
import { EN_GB, NAMESPACES, type Namespace } from './resources';

export { EN_GB, NAMESPACES, keysOf, type Namespace } from './resources';
export {
  BANK_IDS,
  BANK_REPEAT_WINDOW,
  IN_ROUND_BANKS,
  RECEIPT_LINE_CHAR_MAX,
  SLOT_TYPES,
  STATE_BANDS,
  bankEntries,
  bankEntryOf,
  bankLines,
  bankNamespaces,
  lintBanks,
  matchKey,
  messageSlots,
  type BankEntry,
  type BankId,
  type BankLine,
  type BankProblem,
  type Resources,
  type SlotType,
} from './banks';
export {
  BankRefusal,
  checkLine,
  pickLine,
  renderLine,
  type RefusalReason,
  type RenderOptions,
  type ShownWhen,
} from './render';

/** The one locale of version 1. Later languages are a translation job (TON-032). */
export const LOCALE = 'en-GB';

/** A value a message may take. */
export type MessageValue = string | number | boolean | Date | null | undefined;

export interface ContentOptions {
  /** The player's own IANA time zone, for dates and times (TON-034). Defaults to Europe/London. */
  readonly timeZone?: string;
  readonly lengthSystem?: LengthSystem;
  readonly speedUnit?: SpeedUnit;
  readonly temperatureUnit?: TemperatureUnit;
}

export interface Content {
  /** A string by "namespace:key" (or a key of `shell`), with its values. `{gameName}` is always filled. */
  t(key: string, values?: Readonly<Record<string, MessageValue>>): string;
  /** A length held in metres, in the player's units: "150 yards", "6 feet", "137 metres". */
  length(metres: number, purpose?: LengthPurpose, short?: boolean): string;
  /**
   * A range of lengths with the unit once, short form: "142–157 yd" (`25` TON-043; the pattern
   * line's "Carry 148–163"). Where the two ends show the same number, that number alone: "150 yd".
   */
  lengthRange(lo_m: number, hi_m: number, purpose?: LengthPurpose): string;
  /** A speed held in metres per second, in the player's unit. */
  speed(mps: number): string;
  /** A temperature held in °C, in the player's unit. */
  temperature(celsius: number): string;
  /** A UTC time ("…Z"), in the player's time zone: a date, a time, or both. */
  dateTime(utc: string | Date, form?: 'date' | 'time' | 'date_time'): string;
  /** The i18next instance, for the app's React binding. */
  readonly i18n: i18n;
  readonly options: Required<ContentOptions>;
}

/** The ICU formats, in the player's time zone (TON-034). */
function formatsFor(timeZone: string): Record<string, Record<string, Intl.DateTimeFormatOptions>> {
  return {
    date: { day: { day: 'numeric', month: 'long', year: 'numeric', timeZone } },
    time: { clock: { hour: '2-digit', minute: '2-digit', hourCycle: 'h23', timeZone } },
  };
}

/** Makes the locale layer for one player. Synchronous: the strings are bundled. */
export function createContent(options: ContentOptions = {}): Content {
  const opts: Required<ContentOptions> = {
    timeZone: options.timeZone ?? 'Europe/London',
    lengthSystem: options.lengthSystem ?? 'yards',
    speedUnit: options.speedUnit ?? 'mph',
    temperatureUnit: options.temperatureUnit ?? 'c',
  };
  const inst = i18next.createInstance();
  void inst.use(ICU).init({
    lng: LOCALE,
    fallbackLng: LOCALE,
    supportedLngs: [LOCALE],
    load: 'currentOnly',
    ns: [...NAMESPACES],
    defaultNS: 'shell' satisfies Namespace,
    resources: { [LOCALE]: EN_GB },
    initAsync: false,
    returnNull: false,
    interpolation: { escapeValue: false },
    i18nFormat: { formats: formatsFor(opts.timeZone) },
  });
  const t = (key: string, values: Readonly<Record<string, MessageValue>> = {}): string =>
    inst.t(key, { ...values, gameName: GAME_NAME }) as string;
  return {
    t,
    length: (metres, purpose = 'golf', short = false) => {
      const d = displayLength(metres, opts.lengthSystem, purpose);
      return t(short ? 'units:length_short' : 'units:length', { value: d.value, unit: d.unit });
    },
    lengthRange: (lo_m, hi_m, purpose = 'golf') => {
      const a = displayLength(lo_m, opts.lengthSystem, purpose);
      const b = displayLength(hi_m, opts.lengthSystem, purpose);
      if (a.value === b.value) return t('units:length_short', { value: a.value, unit: a.unit });
      return t('units:length_range_short', { lo: a.value, hi: b.value, unit: a.unit });
    },
    speed: (mps) => {
      const d = displaySpeed(mps, opts.speedUnit);
      return t('units:speed', { value: d.value, unit: d.unit });
    },
    temperature: (celsius) => {
      const d = displayTemperature(celsius, opts.temperatureUnit);
      return t('units:temperature', { value: d.value, unit: d.unit });
    },
    dateTime: (utc, form = 'date_time') =>
      t('time:' + form, { when: typeof utc === 'string' ? new Date(utc) : utc }),
    i18n: inst,
    options: opts,
  };
}
